import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ShieldCheckIcon, MapPinIcon, XIcon } from 'lucide-react';
const NAMES = ['María', 'Sofía', 'Camila', 'Valentina', 'Lucía', 'Florencia', 'Antonella', 'Mariana', 'Belén', 'Agustina', 'Martina', 'Julieta', 'Brenda', 'Carolina', 'Romina', 'Daniela', 'Ailén', 'Catalina', 'Micaela', 'Macarena', 'Rocío', 'Tamara', 'Paula', 'Yamila', 'Ivana', 'Gabriela', 'Noelia', 'Constanza', 'Pilar', 'Abril', 'Delfina', 'Renata'];
const LAST_INITIALS = ['G.', 'M.', 'L.', 'R.', 'S.', 'T.', 'A.', 'C.', 'F.', 'P.', 'V.', 'B.', 'D.'];
const CITIES = ['Buenos Aires', 'CABA', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Mar del Plata', 'San Miguel de Tucumán', 'Salta', 'Santa Fe', 'San Juan', 'Resistencia', 'Neuquén', 'Posadas', 'Bahía Blanca', 'Corrientes', 'Paraná', 'San Luis', 'Bariloche', 'Tandil', 'Pilar', 'Quilmes', 'San Isidro', 'Olavarría', 'Concordia', 'Río Cuarto', 'Comodoro Rivadavia', 'Catamarca', 'San Rafael', 'Villa Carlos Paz'];
const OFFERS = [{
  name: 'Mujer Expansiva Premium',
  isPremium: true
}, {
  name: 'Mujer Expansiva Premium',
  isPremium: true
}, {
  name: 'Mujer Expansiva',
  isPremium: false
}];
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function randomBetween(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
interface Notif {
  id: number;
  name: string;
  city: string;
  offer: string;
  isPremium: boolean;
}
function makeNotif(): Notif {
  const offer = pick(OFFERS);
  return {
    id: Date.now() + Math.random(),
    name: `${pick(NAMES)} ${pick(LAST_INITIALS)}`,
    city: pick(CITIES),
    offer: offer.name,
    isPremium: offer.isPremium
  };
}
export function PurchaseNotification() {
  const [current, setCurrent] = useState<Notif | null>(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    let showTimer: number | undefined;
    let hideTimer: number | undefined;
    const cycle = () => {
      // Show a new notification
      setCurrent(makeNotif());
      // Auto-hide after 5.5s
      hideTimer = window.setTimeout(() => {
        setCurrent(null);
        // Wait 45-60 seconds before the next one
        const nextDelay = randomBetween(45000, 60000);
        showTimer = window.setTimeout(cycle, nextDelay);
      }, 5500);
    };
    // First notification appears 8 seconds after page load
    showTimer = window.setTimeout(cycle, 8000);
    return () => {
      if (showTimer) window.clearTimeout(showTimer);
      if (hideTimer) window.clearTimeout(hideTimer);
    };
  }, []);
  // If user dismisses, hide forever this session
  if (dismissed) return null;
  return <div className="fixed bottom-3 left-3 md:bottom-5 md:left-5 z-50 pointer-events-none" data-id="element-252">
      <AnimatePresence mode="wait" data-id="element-253">
        {current && <motion.div key={current.id} initial={{
        opacity: 0,
        x: -40,
        y: 10
      }} animate={{
        opacity: 1,
        x: 0,
        y: 0
      }} exit={{
        opacity: 0,
        x: -40,
        y: 10
      }} transition={{
        duration: 0.45,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="pointer-events-auto max-w-[280px] md:max-w-[320px]" data-id="element-254">
            <div className="relative bg-cream border border-plum/10 rounded-lg shadow-2xl shadow-plum/15 overflow-hidden" data-id="element-255">
              {/* Gold accent strip on the left */}
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${current.isPremium ? 'bg-gold' : 'bg-plum'}`} data-id="element-256" />

              {/* Dismiss button */}
              <button onClick={() => setDismissed(true)} aria-label="Cerrar notificación" className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full hover:bg-plum/10 transition-colors text-plum/40 hover:text-plum" data-id="element-257">
                <XIcon size={12} data-id="element-258" />
              </button>

              <div className="flex items-center gap-3 p-3 pl-4 pr-7" data-id="element-259">
                {/* Avatar circle with check */}
                <div className="relative flex-shrink-0" data-id="element-260">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-serif font-bold text-base ${current.isPremium ? 'bg-gold text-white' : 'bg-plum text-cream'}`} data-id="element-261">
                    {current.name.charAt(0)}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-cream flex items-center justify-center" data-id="element-262">
                    <CheckIcon size={8} className="text-white" strokeWidth={4} data-id="element-263" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0" data-id="element-264">
                  <div className="flex items-center gap-1 mb-0.5" data-id="element-265">
                    <ShieldCheckIcon size={10} className="text-green-600 flex-shrink-0" data-id="element-266" />
                    <span className="text-[9px] font-bold uppercase tracking-wider text-green-700" data-id="element-267">
                      Compra verificada
                    </span>
                  </div>
                  <p className="text-[12px] text-plum leading-tight" data-id="element-268">
                    <span className="font-bold" data-id="element-269">{current.name}</span>
                    <span className="text-plum/70" data-id="element-270"> acaba de comprar</span>
                  </p>
                  <p className={`text-[11px] font-semibold leading-tight truncate ${current.isPremium ? 'text-gold' : 'text-plum/85'}`} data-id="element-271">
                    {current.offer}
                  </p>
                  <div className="flex items-center gap-1 mt-0.5 text-[10px] text-plum/50" data-id="element-272">
                    <MapPinIcon size={9} data-id="element-273" />
                    <span className="truncate" data-id="element-274">{current.city}</span>
                    <span data-id="element-275">·</span>
                    <span className="text-green-700 font-semibold" data-id="element-276">
                      ahora mismo
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}